import React from "react";
import { rubricColor, dimensionLabel } from "../lib/scoring";

const LEVELS = ["Strong", "Developing", "Emerging", "At Risk"];

export default function Rubric({ rubric, scores }) {
  if (!rubric || rubric.suppressed) {
    return (
      <div className="panel" data-testid="rubric-suppressed">
        <div className="eyebrow mb-2 text-gray-500">Rubric distribution</div>
        <p className="text-sm text-gray-700">
          Need at least 5 completed respondents to show rubric distributions. Currently:{" "}
          <strong>{rubric?.n ?? 0}</strong>.
        </p>
      </div>
    );
  }
  const dist = rubric.rubric_distribution || {};
  const accum = rubric.developing_accumulation || {};
  const total = rubric.n;

  return (
    <div className="space-y-12" data-testid="rubric">
      <div>
        <div className="eyebrow mb-1">Rubric distribution</div>
        <h3 className="text-lg mb-6 text-gray-700" style={{ fontFamily: "Inter", fontWeight: 500, color: "#1A1A1A" }}>
          How {total} respondents land across the four bands, per dimension.
        </h3>

        <div className="space-y-3">
          {Object.entries(dist).map(([dim, counts]) => {
            const dimTotal = Object.values(counts).reduce((a, b) => a + b, 0);
            return (
              <div key={dim} className="grid grid-cols-12 gap-3 items-center" data-testid={`rubric-row-${dim}`}>
                <div className="col-span-3 text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>
                  {dimensionLabel(dim)}
                </div>
                <div className="col-span-9 flex h-8 border border-gray-100">
                  {LEVELS.map((lv) => {
                    const n = counts[lv] || 0;
                    if (n === 0) return null;
                    const pct = (n / dimTotal) * 100;
                    const c = rubricColor(lv);
                    return (
                      <div
                        key={lv}
                        className="flex items-center justify-center text-xs"
                        style={{
                          width: `${pct}%`,
                          background: c.solid,
                          color: "#FBFAF7",
                          fontFamily: "Oswald",
                          letterSpacing: "0.05em",
                        }}
                        title={`${lv}: ${n} respondents (${Math.round(pct)}%)`}
                      >
                        {pct >= 12 && n}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Accumulation accumulation={accum} total={total} />

      {scores && !scores.suppressed && <LowestItems items={scores.items} />}
    </div>
  );
}

function Accumulation({ accumulation, total }) {
  const max = Math.max(...Object.values(accumulation), 1);
  return (
    <div data-testid="developing-accumulation">
      <div className="eyebrow mb-1">Developing × accumulation</div>
      <p className="text-sm text-gray-700 mb-6 max-w-prose">
        How many respondents land in <em>Developing</em> across N dimensions. The right tail is where
        the cohort tools (Sprint 5) start to focus attention.
      </p>
      <div className="grid grid-cols-8 gap-2 items-end" style={{ minHeight: "180px" }}>
        {Array.from({ length: 8 }).map((_, n) => {
          const count = accumulation[n] || 0;
          const heightPct = (count / max) * 100;
          return (
            <div key={n} className="flex flex-col items-center justify-end" data-testid={`accum-${n}`}>
              <div
                className="w-full transition-all duration-200"
                style={{
                  height: `${heightPct}%`,
                  minHeight: count > 0 ? "8px" : "2px",
                  background: count > 0 ? "#2563EB" : "#EEEEEE",
                }}
                title={`${count} respondent${count === 1 ? "" : "s"} are Developing in ${n} dimension${n === 1 ? "" : "s"}`}
              />
              <div
                className="text-xs mt-2"
                style={{ fontFamily: "Oswald", letterSpacing: "0.1em", color: "#555555" }}
              >
                {n}
              </div>
              <div className="text-xs text-gray-500">{count}</div>
            </div>
          );
        })}
      </div>
      <div className="text-xs text-gray-500 mt-2 text-center" style={{ fontFamily: "Oswald", letterSpacing: "0.15em" }}>
        Number of dimensions in Developing
      </div>
    </div>
  );
}

function LowestItems({ items }) {
  if (!items) return null;
  const sorted = [...items].sort((a, b) => a.scored_mean - b.scored_mean).slice(0, 5);
  return (
    <div data-testid="lowest-items">
      <div className="eyebrow mb-1">Five lowest items</div>
      <p className="text-sm text-gray-700 mb-6 max-w-prose">
        Ranked by scored mean. Reverse items are corrected — these are the items where the cohort is
        most under-resourced, regardless of phrasing.
      </p>
      <div className="space-y-2">
        {sorted.map((it, idx) => (
          <div
            key={it.item}
            className="flex items-baseline gap-4 py-3 px-4 bg-cream border border-gray-100"
            data-testid={`lowest-item-${it.item}`}
          >
            <div className="text-xs text-gray-500" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
              {String(idx + 1).padStart(2, "0")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-gray-500" style={{ fontFamily: "Oswald", letterSpacing: "0.15em" }}>
                Item {it.item} · {dimensionLabel(it.dimension)} {it.reverse ? "· reverse-keyed" : ""}
              </div>
            </div>
            <div className="text-sm shrink-0 text-right">
              <div style={{ fontFamily: "Oswald", letterSpacing: "0.1em", color: "#1A1A1A" }}>
                {it.scored_mean.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500">scored mean</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
